import { useCallback, useEffect, useState } from "react";
import { api, pesanGalat } from "../../lib/api";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";
import { formatTanggal } from "../../lib/tanggal";

/**
 * Saklar jawaban Bos no. 23 (Q-HARGA-IMEI, brief 22 Sep 2026 G2) — KHUSUS
 * superadmin, sama dengan gerbang backend `require_role("superadmin")` di
 * `routers/inventory/info_unit.py`.
 *
 *   (a) kolom harga per IMEI + isi otomatis di kasir  → keduanya hidup (bawaan)
 *   (b) kolom tampil, kasir memakai harga produk       → matikan "Isi otomatis"
 *   (c) tanpa harga per IMEI                           → matikan kolomnya
 * Mematikan kolom TIDAK menghapus harga yang sudah diisi — hanya diabaikan,
 * jadi bisa dihidupkan kembali tanpa mengetik ulang.
 */
// Di luar komponen: didefinisikan di dalam render, React menganggapnya komponen
// BARU tiap render (dipasang ulang, fokus checkbox hilang).
function Saklar({ nilai, ubah, testid, judul, ket, mati }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border ${mati ? "opacity-50" : ""} border-slate-200`}>
      <input type="checkbox" checked={nilai} disabled={mati} onChange={(e) => ubah(e.target.checked)}
        data-testid={testid} className="mt-1 w-4 h-4" />
      <span>
        <span className="block text-sm font-medium text-slate-800">{judul}</span>
        <span className="block text-xs text-slate-500">{ket}</span>
      </span>
    </label>
  );
}

export default function PengaturanHargaUnit({ onClose, onTersimpan }) {
  const [data, setData] = useState(null);
  const [kolom, setKolom] = useState(true);
  const [kasir, setKasir] = useState(true);
  const [simpan, setSimpan] = useState(false);

  const muat = useCallback(async () => {
    try {
      const r = await api.get("/inventory/price-list/pengaturan-harga-unit");
      setData(r.data);
      setKolom(!!r.data.kolom_aktif);
      setKasir(!!r.data.pakai_di_kasir);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat pengaturan harga per IMEI"));
    }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  const kirim = async () => {
    setSimpan(true);
    try {
      const r = await api.put("/inventory/price-list/pengaturan-harga-unit",
        { versi: data?.versi || 0, kolom_aktif: kolom, pakai_di_kasir: kolom && kasir });
      setData(r.data);
      toast.success("Pengaturan harga per IMEI disimpan");
      onTersimpan?.();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan pengaturan"));
      if (e?.response?.status === 409) muat();
    } finally {
      setSimpan(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4" data-testid="pl-panel-harga-unit">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Atur Harga Jual per IMEI</h2>
          <button type="button" onClick={onClose} data-testid="pl-panel-harga-unit-tutup"
            className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-500">
          Menjawab pertanyaan no. 23: harga jual berbeda per IMEI untuk produk second.
          Bawaannya pilihan (a) — kolom harga per IMEI aktif dan harganya terisi otomatis saat
          kasir memilih IMEI (masih bisa diubah kasir).
        </p>
        {data?.catatan && (
          <div className="flex gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /> {data.catatan}
          </div>
        )}
        {!data ? <p className="text-sm text-slate-500">Memuat…</p> : (
          <>
            <Saklar nilai={kolom} ubah={(v) => { setKolom(v); if (!v) setKasir(false); }}
              testid="pl-harga-unit-kolom" judul="Kolom harga jual per IMEI"
              ket="Harga per IMEI bisa diisi di Price List (kosong = harga produk)." />
            <Saklar nilai={kolom && kasir} ubah={setKasir} mati={!kolom}
              testid="pl-harga-unit-kasir" judul="Isi otomatis di nota"
              ket="Saat memilih IMEI di Input Nota (termasuk draf yang dibuka kasir), harga baris diisi harga IMEI itu — masih bisa diubah kasir." />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} data-testid="pl-panel-harga-unit-tutup-bawah"
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm">Tutup</button>
              <button type="button" onClick={kirim} disabled={simpan} data-testid="pl-harga-unit-simpan"
                className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60">
                {simpan ? "Menyimpan…" : "Simpan"}
              </button>
            </div>
            {(data.riwayat || []).length > 0 && (
              <div className="border-t border-slate-100 pt-2">
                <p className="text-xs font-semibold text-slate-600 mb-1">Riwayat</p>
                <ul className="space-y-1 text-[11px] text-slate-500" data-testid="pl-harga-unit-riwayat">
                  {data.riwayat.map((r) => (
                    <li key={r.versi}>
                      {formatTanggal(r.pada, { denganJam: true })} · {r.oleh_email} · kolom{" "}
                      {r.sesudah?.kolom_aktif ? "aktif" : "mati"}, isi otomatis{" "}
                      {r.sesudah?.pakai_di_kasir ? "aktif" : "mati"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
